// eslint-disable-next-line
// @ts-nocheck
import calendarCSS from '../../styles/less/components/calendar.less';
import intl from '../core/intl';
import getResource from '../core/resources';
import processKeyboardEvent from '../core/keyboardEvent';

const stylesheet = new CSSStyleSheet();
stylesheet.replaceSync(calendarCSS);
const calendarClass = 'aras-calendar';
const switchButtonClass = 'aras-button aras-button_d';
const arrowClass = 'aras-icon-arrow';
const gridItemClass = `${calendarClass}__grid-item`;
const secondaryItemClass = `${gridItemClass}_secondary`;
const weekLength = 7;
const weeksNumber = 6;
const monthsInYear = 12;
const yearsInDecade = 10;
const MODES = ['days', 'months', 'years'];
const ariaLabels = {
	prev: {
		days: 'calendar.aria_label.prev_month',
		months: 'calendar.aria_label.prev_year',
		years: 'calendar.aria_label.prev_decade'
	},
	next: {
		days: 'calendar.aria_label.next_month',
		months: 'calendar.aria_label.next_year',
		years: 'calendar.aria_label.next_decade'
	}
};

export default class Calendar extends HyperHTMLElement {
	static get observedAttributes() {
		return ['selected-date', 'mode', 'range-date', 'range-mode'];
	}

	attributeChangedCallback(name, oldValue, newValue) {
		let newState;
		switch (name) {
			case 'selected-date':
				newState = {
					selectedDate: newValue,
					focusedDate: newValue
						? newValue
						: intl.date.toIS0Format(this._getDate())
				};
				break;
			case 'mode':
				if (MODES.includes(newValue)) {
					newState = { mode: newValue };
				}
				break;
			case 'range-date':
				newState = { rangeDate: newValue };
				if (newValue) {
					newState.focusedDate = newValue;
				}
				break;
			case 'range-mode':
				newState = {
					rangeMode: newValue
				};
				break;
		}
		if (newState) {
			this.setState(newState);
		}
	}

	created() {
		this.classList.add(calendarClass);
		this.attachShadow({ mode: 'open' });
		this.shadowRoot.adoptedStyleSheets = [stylesheet];
		this.setState({
			focusedDate: intl.date.toIS0Format(this._getDate()),
			selectedDate: '',
			mode: this.mode || 'days',
			rangeDate: '',
			rangeMode: '=',
			hoveredDate: ''
		});
	}

	render() {
		const isDateMode = this.state.mode === 'days';
		const formatOptions = isDateMode ? { month: 'long' } : {};
		const title = this._getButtonModeTitle(formatOptions);
		const switchButtonIconRef = (
			<svg style="display: none;">
				<symbol id="svg-calendarselect" viewBox="0 0 48 48">
					<g fill="#555">
						<path d="M36 34h-4v-4h4zM30 34h-4v-4h4zM36 28h-4v-4h4zM30 28h-4v-4h4zM36 22h-4v-4h4zM30 22h-4v-4h4zM24 22h-4v-4h4zM18 22h-4v-4h4zM36 16H14v-4h22zM12 26v2h6l-6 6v2h2l6-6v6h2V26z" />
					</g>
				</symbol>
			</svg>
		);
		const switchButtonIconNode = (
			<svg class={'aras-button__icon'}>
				<use xlinkHref="#svg-calendarselect"></use>
			</svg>
		);
		const switchModeButton = (
			<button
				className={`${switchButtonClass} ${calendarClass}__switch_mode`}
				onclick={this._switchMode}
			>
				<span className="aras-button__text" aria-live="polite">
					{title}
				</span>
				{switchButtonIconRef}
				{this.state.mode === 'years' ? null : switchButtonIconNode}
			</button>
		);
		const switchToPrevLabel = getResource(ariaLabels['prev'][this.state.mode]);
		const switchToNextLabel = getResource(ariaLabels['next'][this.state.mode]);
		const switchNode = (
			<div className={`${calendarClass}__switch`}>
				{switchModeButton}
				<button
					aria-label={switchToPrevLabel}
					className={`${switchButtonClass} ${calendarClass}__switch_prev ${arrowClass} ${arrowClass}_left`}
					onclick={this._arrowButtonClick}
				/>
				<button
					aria-label={switchToNextLabel}
					className={`${switchButtonClass} ${calendarClass}__switch_next ${arrowClass} ${arrowClass}_right`}
					onclick={this._arrowButtonClick}
				/>
			</div>
		);
		const gridItems = this._getGridItems();
		const gridClass = `${calendarClass}__grid`;
		const daysModifier = isDateMode ? ` ${gridClass}_days` : '';
		const className = `${gridClass}${daysModifier}`;
		const isHighlightActive =
			this.state.rangeMode === '...' &&
			this.state.mode === 'days' &&
			!this.state.rangeDate &&
			this.state.selectedDate;
		const onmouseover = isHighlightActive
			? (e) => this._highlightRange(e)
			: null;
		const grid = (
			<div
				className={className}
				onclick={this._selectDate}
				onmouseover={onmouseover}
				onkeydown={this._keyboardHandler}
			>
				{gridItems}
			</div>
		);
		const root = (
			<Fragment>
				{switchNode}
				{grid}
			</Fragment>
		);
		Inferno.render(root, this.shadowRoot);
	}

	_calculateDates() {
		const daysOfWeek = this._getLocaleDaysOfWeek();
		const datesOfPreviousMonth = this._getDatesOfPreviousMonth();
		const datesOfCurrentMonth = this._getDatesOfCurrentMonth();
		const datesOfNextMonth = this._getDatesOfNextMonth(
			datesOfPreviousMonth.length,
			datesOfCurrentMonth.length
		);
		const datesWithWeeks = this._getDatesWithWeeks(
			datesOfPreviousMonth,
			datesOfCurrentMonth,
			datesOfNextMonth
		);

		return [{ value: '#', disabled: true }, ...daysOfWeek, ...datesWithWeeks];
	}

	_calculateGridData() {
		if (this.state.mode === 'days') {
			return this._calculateDates();
		}
		return this._calculateMonthsYears();
	}

	_calculateMonthsYears() {
		const currentDate = this._getDateObject(true);
		const focusedDate = this._getDateObject();
		const items = [];
		const isMonthsMode = this.state.mode === 'months';
		const itemsCount = isMonthsMode ? monthsInYear : yearsInDecade;
		const decade = Math.trunc(focusedDate.year / 10);
		for (let data = 0; data < itemsCount; data++) {
			const targetYear = decade * 10 + data;
			const date = isMonthsMode
				? new Date(focusedDate.year, data, 1)
				: new Date(targetYear, focusedDate.month, 1);
			const isCurrent = isMonthsMode
				? this._compareDates(date, currentDate.date, 'months') === 0
				: this._compareDates(date, currentDate.date, 'years') === 0;
			const isFocused = isMonthsMode
				? this._compareDates(date, focusedDate.date, 'months') === 0
				: this._compareDates(date, focusedDate.date, 'years') === 0;
			const value = isMonthsMode
				? this._format(date, { month: 'short' })
				: targetYear;
			items.push({
				data: isMonthsMode ? data : targetYear,
				value,
				isCurrent,
				isFocused
			});
		}

		return items;
	}

	_prepareDate(date, mode) {
		return new Date(
			date.getFullYear(),
			mode === 'years' ? 0 : date.getMonth(),
			mode === 'days' ? date.getDate() : 1
		);
	}

	_compareDates(date1, date2, mode = 'days') {
		const _date1 = this._prepareDate(date1, mode);
		const _date2 = this._prepareDate(date2, mode);

		if (_date1 > _date2) {
			return 1;
		}
		if (_date1 < _date2) {
			return -1;
		}
		return 0;
	}

	_createRange(start, end) {
		return Array.from(
			new Array(end - start + 1),
			(item, index) => start + index
		);
	}

	_format(date, options) {
		date = Date.UTC(
			date.getFullYear(),
			date.getMonth(),
			date.getDate(),
			date.getHours(),
			date.getMinutes(),
			date.getSeconds(),
			date.getMilliseconds()
		);
		options = {
			...options,
			timeZone: 'UTC'
		};
		const result = new Intl.DateTimeFormat(intl.locale, options).format(date);

		return result;
	}

	_getButtonModeTitle(additionalFormat = {}) {
		const formatOptions = { year: 'numeric', ...additionalFormat };

		if (this.state.mode === 'years') {
			const focusedDate = this._getDateObject();
			const decade = Math.trunc(focusedDate.year / 10);
			const firstDate = new Date(`${decade}0`, 0, 1);
			const firstYear = this._format(firstDate, { year: 'numeric' });
			const lastDate = new Date(`${decade}9`, 0, 1);
			const lastYear = this._format(lastDate, { year: 'numeric' });
			return `${firstYear} - ${lastYear}`;
		}

		const date = intl.date.parse(this.state.focusedDate);
		return this._format(date, formatOptions);
	}

	_getDateObject(current) {
		const date = current
			? this._getDate()
			: intl.date.parse(this.state.focusedDate);
		const year = date.getFullYear();
		const month = date.getMonth();
		const weekDay = date.getDay();
		const day = date.getDate();

		return {
			date,
			year,
			month,
			day,
			weekDay
		};
	}

	_getDate(year, month, day) {
		const currentDate = new Date();
		year = year ?? currentDate.getFullYear();
		month = month ?? currentDate.getMonth();
		day = day ?? currentDate.getDate();
		return new Date(year, month, day);
	}

	_getDatesOfCurrentMonth() {
		const currentDate = this._getDateObject(true);
		const firstDateOfMonth = 1;
		const focusedDate = this._getDateObject();
		const lastDateOfMonth = this._getLastDateOfMonth(focusedDate.month);
		const datesOfCurrentMonth = this._createRange(
			firstDateOfMonth,
			lastDateOfMonth
		).map((date) => {
			const dateValue = new Date(focusedDate.year, focusedDate.month, date);
			const isCurrent = this._compareDates(currentDate.date, dateValue) === 0;
			const isFocused = this._compareDates(focusedDate.date, dateValue) === 0;
			let isSelected = null;
			let inRange = null;
			if (this.state.selectedDate) {
				const selectedDate = intl.date.parse(this.state.selectedDate);
				isSelected = this._compareDates(dateValue, selectedDate) === 0;
				if (!isSelected && this.state.rangeDate) {
					const rangeDate = intl.date.parse(this.state.rangeDate);
					isSelected = this._compareDates(dateValue, rangeDate) === 0;
				}
				inRange = this._isInRange(dateValue);
			}
			return {
				isCurrent,
				isSelected,
				isFocused,
				inRange,
				value: date
			};
		});

		return datesOfCurrentMonth;
	}

	_getDatesOfNextMonth(datesOfCurrentMonthLength, datesOfPreviousMonthLength) {
		const firstDateOfMonth = 1;
		const daysCount = weeksNumber * weekLength;
		const listedDaysCount =
			datesOfCurrentMonthLength + datesOfPreviousMonthLength;
		const restOfNextDates = daysCount - listedDaysCount;
		const focusedDate = this._getDateObject();
		const datesOfNextMonth = this._createRange(
			firstDateOfMonth,
			restOfNextDates
		).map((date) => {
			const dateValue = new Date(focusedDate.year, focusedDate.month + 1, date);
			const inRange = this.state.selectedDate
				? this._isInRange(dateValue)
				: null;
			return { value: date, notInMonth: true, monthOffset: 1, inRange };
		});

		return datesOfNextMonth;
	}

	_getDatesOfPreviousMonth() {
		const firstDayOfWeek = intl.date.firstDayOfWeek();
		const focusedDate = this._getDateObject();
		const currentDate = this._getDate(focusedDate.year, focusedDate.month, 1);
		let restOfPreviousDates = currentDate.getDay() - firstDayOfWeek;
		restOfPreviousDates =
			restOfPreviousDates < 0
				? weekLength + restOfPreviousDates
				: restOfPreviousDates;
		const lastOfPreviousDates = this._getLastDateOfMonth(focusedDate.month - 1);
		const datesOfPreviousMonth = this._createRange(
			lastOfPreviousDates - restOfPreviousDates + 1,
			lastOfPreviousDates
		).map((date) => {
			const dateValue = new Date(focusedDate.year, focusedDate.month - 1, date);
			const inRange = this.state.selectedDate
				? this._isInRange(dateValue)
				: null;
			return { value: date, notInMonth: true, monthOffset: -1, inRange };
		});

		return datesOfPreviousMonth;
	}

	_getDatesWithWeeks(
		datesOfPreviousMonth,
		datesOfCurrentMonth,
		datesOfNextMonth
	) {
		const isWeek = true;
		const disabled = true;
		const focusedDate = this._getDateObject();
		const firstDateOfMonth = this._getDate(
			focusedDate.year,
			focusedDate.month,
			1
		);
		const firstWeekOfMonth = intl.date.week(firstDateOfMonth);
		const datesWithWeeks = [
			...datesOfPreviousMonth,
			...datesOfCurrentMonth,
			...datesOfNextMonth
		].reduce(
			(arr, date, index) => {
				if (index > 0 && index % weekLength === 0) {
					const month = date.notInMonth
						? focusedDate.month + 1
						: focusedDate.month;
					const currentDate = this._getDate(
						focusedDate.year,
						month,
						date.value
					);
					const week = intl.date.week(currentDate);
					arr.push({
						value: week,
						isWeek,
						disabled
					});
				}

				arr.push(date);

				return arr;
			},
			[
				{
					value: firstWeekOfMonth,
					isWeek,
					disabled
				}
			]
		);

		return datesWithWeeks;
	}

	_getGridItems() {
		const isDateMode = this.state.mode === 'days';
		const monthClass = !isDateMode ? ` ${secondaryItemClass}` : '';
		const gridData = this._calculateGridData();

		return gridData.map((item) => {
			const {
				value,
				notInMonth,
				isCurrent,
				isSelected,
				isFocused,
				inRange,
				isWeek,
				disabled,
				data,
				monthOffset = null
			} = item;
			const notInMonthClass = notInMonth
				? ` ${gridItemClass}_not-in-month`
				: '';
			const weekClass = isWeek ? ` ${gridItemClass}_week` : '';
			const disabledClass = disabled ? ` ${gridItemClass}_disabled` : '';
			const isCurrentClass = isCurrent ? ` ${gridItemClass}_current` : '';
			const isSelectedClass = isSelected ? ` ${gridItemClass}_selected` : '';
			const inRangeClass = inRange ? ` ${gridItemClass}_in-range` : '';
			const className = `${gridItemClass}${notInMonthClass}${isCurrentClass}${weekClass}${disabledClass}${monthClass}${inRangeClass}${isSelectedClass}`;
			const ariaRole = disabled ? null : 'button';
			const tabIndex = isFocused ? '0' : null;
			let dataValue = null;
			if (!disabled) {
				dataValue = !isDateMode ? data : value;
			}
			return (
				<div
					className={className}
					tabIndex={tabIndex}
					data-value={dataValue}
					data-offset={monthOffset}
					role={ariaRole}
				>
					{value}
				</div>
			);
		});
	}

	_getLastDateOfMonth(month) {
		const { year } = this._getDateObject();
		const date = this._getDate(year, month + 1, 0);

		return date.getDate();
	}

	_getLocaleDaysOfWeek() {
		const formatOptions = { weekday: 'narrow' };
		const currentDate = this._getDateObject(true);

		const firstDayOfWeek = intl.date.firstDayOfWeek();
		const firstDateOfWeek =
			currentDate.date.getDate() - currentDate.weekDay + firstDayOfWeek;
		const datesOfWeek = this._createRange(
			firstDateOfWeek,
			firstDateOfWeek + weekLength - 1
		);

		return datesOfWeek.map((date) => {
			const day = this._format(
				new Date(currentDate.year, currentDate.month, date),
				formatOptions
			);

			return {
				value: day,
				disabled: true
			};
		});
	}

	_getOffset() {
		let offset = monthsInYear;
		switch (this.state.mode) {
			case 'days':
				offset = 1;
				break;
			case 'years':
				offset *= yearsInDecade;
		}
		return offset;
	}

	_arrowButtonClick = (e) => {
		const next = e.target.closest(`.${calendarClass}__switch_next`);
		const focusedDate = this._getDateObject();
		const offset = this._getOffset();
		const month = next
			? focusedDate.month + offset
			: focusedDate.month - offset;
		const date = this._getDate(focusedDate.year, month, 1);
		const iso = intl.date.toIS0Format(date);
		this.setState({ focusedDate: iso });
	};

	_highlightRange(e) {
		const target = e.target.closest(`.${gridItemClass}`);
		const isDisabled = target.classList.contains(`${gridItemClass}_disabled`);
		if (isDisabled) {
			return;
		}

		const focusedDate = intl.date.parse(this.state.focusedDate);
		const { value, offset = 0 } = target.dataset;
		const hoveredDate = new Date(
			focusedDate.getFullYear(),
			focusedDate.getMonth() + +offset,
			value
		);
		const hoveredIso = intl.date.toIS0Format(hoveredDate);
		this.setState({ hoveredDate: hoveredIso });
	}

	_isInRange(date) {
		const selectedDate = intl.date.parse(this.state.selectedDate);
		if (this.state.rangeMode === '...') {
			const endRangeDate = this.state.rangeDate || this.state.hoveredDate;
			if (endRangeDate) {
				const rangeDate = intl.date.parse(endRangeDate);
				const dates = [selectedDate, rangeDate];
				const minDate = new Date(Math.min(...dates));
				const maxDate = new Date(Math.max(...dates));
				return minDate <= date && date <= maxDate;
			}

			return false;
		}

		const comparisonResult = this._compareDates(
			date,
			selectedDate,
			this.state.mode
		);

		switch (this.state.rangeMode) {
			case '<':
				return comparisonResult === -1;
			case '<=':
				return comparisonResult !== 1;
			case '>':
				return comparisonResult === 1;
			case '>=':
				return comparisonResult !== -1;
			case '=':
				return comparisonResult === 0;
		}

		return false;
	}

	_selectDate = (e) => {
		const focusedDate = this._getDateObject();
		const target = e.target.closest(`.${gridItemClass}`);

		if (!target || !target.dataset.value) {
			return;
		}

		const isDisabled = target.classList.contains(`${gridItemClass}_disabled`);
		if (isDisabled) {
			return;
		}

		const value = target.dataset.value;
		if (this.state.mode === 'days') {
			const { offset = 0 } = target.dataset;
			const selectedDate = new Date(
				focusedDate.year,
				focusedDate.month + +offset,
				value
			);
			const isoDate = intl.date.toIS0Format(selectedDate);

			if (this.state.rangeMode === '...') {
				if (!this.selectedDate || this.rangeDate) {
					this.selectedDate = isoDate;
					this.rangeDate = '';
				} else {
					this.rangeDate = isoDate;
					const selectedDateValue = intl.date.parse(this.selectedDate);
					if (selectedDate < selectedDateValue) {
						this.rangeDate = this.selectedDate;
						this.selectedDate = isoDate;
					}
				}
			} else {
				this.selectedDate = isoDate;
			}
			const event = new CustomEvent('change', {
				detail: {
					selectedDate: this.selectedDate,
					rangeDate: this.rangeDate
				}
			});
			this.dispatchEvent(event);
		} else {
			const isMonthsMode = this.state.mode === 'months';
			const newDate = isMonthsMode
				? this._getDate(focusedDate.year, value, 1)
				: this._getDate(value, focusedDate.month, 1);
			const newMode = isMonthsMode ? 'days' : 'months';
			this._changeMode(newMode);
			this.setState({
				focusedDate: intl.date.toIS0Format(newDate)
			});
		}
	};

	_switchMode = () => {
		const currentMode = this.state.mode;
		const targetMode = currentMode === 'days' ? 'months' : 'years';
		this._changeMode(targetMode);
	};

	_changeMode(mode) {
		this.mode = mode;
		const event = new CustomEvent('changemode', {
			detail: {
				mode
			}
		});
		this.dispatchEvent(event);
	}

	_keyboardHandler = (e) => {
		const { shortcut } = processKeyboardEvent(e);
		const submit = ['space', 'enter'].includes(shortcut);
		if (submit) {
			this._selectDate(e);
		} else {
			let nextFocusedDate;
			if (this.state.mode === 'days') {
				nextFocusedDate = this._getNextFocusedDay(shortcut);
			} else if (this.state.mode === 'months') {
				nextFocusedDate = this._getNextFocusedMonth(shortcut);
			} else if (this.state.mode === 'years') {
				nextFocusedDate = this._getNextFocusedYear(shortcut);
			}

			if (!nextFocusedDate) {
				return;
			}

			this.setState({
				focusedDate: intl.date.toIS0Format(nextFocusedDate)
			});
		}

		e.stopPropagation();
		e.preventDefault();

		const focusNode = this.shadowRoot.querySelector(
			`.${gridItemClass}[tabindex="0"]`
		);
		focusNode.focus();
	};

	_getNextFocusedDay(shortcut) {
		const { date, year, month, day } = this._getDateObject();
		switch (shortcut) {
			case 'ctrl+arrowup':
				this.mode = 'months';
				break;
			case 'arrowleft':
				date.setDate(day - 1);
				break;
			case 'arrowright':
				date.setDate(day + 1);
				break;
			case 'arrowup':
				date.setDate(day - 7);
				break;
			case 'arrowdown':
				date.setDate(day + 7);
				break;
			case 'home':
				date.setDate(1);
				break;
			case 'end':
				date.setMonth(date.getMonth() + 1);
				date.setDate(0);
				break;
			case 'pageup':
				date.setMonth(month - 1);
				// when focus moves from Mar 31 to Feb 28 => Mar 2
				if (month === date.getMonth()) {
					date.setDate(0);
				}
				break;
			case 'pagedown':
				date.setMonth(month + 1);
				// when focus moves from Jan 31 to Feb 28 => Mar 3
				if (date.getMonth() > month + 1) {
					date.setDate(0);
				}
				break;
			case 'shift+pageup':
				date.setFullYear(year - 1);
				// when focus moves from Feb 29 to Feb 28 => Mar 1
				if (month !== date.getMonth()) {
					date.setDate(0);
				}
				break;
			case 'shift+pagedown':
				date.setFullYear(year + 1);
				// when focus moves from Feb 29 to Feb 28 => Mar 1
				if (month !== date.getMonth()) {
					date.setDate(0);
				}
				break;
			default:
				return null;
		}

		return date;
	}

	_getNextFocusedMonth(shortcut) {
		const { date, year, month } = this._getDateObject();
		date.setDate(1);
		switch (shortcut) {
			case 'ctrl+arrowup':
				this.mode = 'years';
				break;
			case 'ctrl+arrowdown':
				this.mode = 'days';
				break;
			case 'arrowleft':
				date.setMonth(month - 1);
				break;
			case 'arrowright':
				date.setMonth(month + 1);
				break;
			case 'arrowup':
				date.setMonth(month - 4);
				break;
			case 'arrowdown':
				date.setMonth(month + 4);
				break;
			case 'home': {
				const january = 0;
				date.setMonth(january);
				break;
			}
			case 'end': {
				const december = 11;
				date.setMonth(december);
				break;
			}
			case 'pageup':
				date.setFullYear(year - 1);
				break;
			case 'pagedown':
				date.setFullYear(year + 1);
				break;
			default:
				return null;
		}

		return date;
	}

	_getNextFocusedYear(shortcut) {
		const { date, year } = this._getDateObject();
		date.setDate(1);
		switch (shortcut) {
			case 'ctrl+arrowdown':
				this.mode = 'months';
				break;
			case 'arrowleft':
				date.setFullYear(year - 1);
				break;
			case 'arrowright':
				date.setFullYear(year + 1);
				break;
			case 'arrowup': {
				const yearDigit = year % 10;
				let modifier = -4;
				switch (yearDigit) {
					case 0:
					case 1:
						modifier = -2;
						break;
					case 2:
					case 3:
						modifier = -6;
						break;
				}
				date.setFullYear(year + modifier);
				break;
			}
			case 'arrowdown': {
				const yearDigit = year % 10;
				let modifier = +4;
				switch (yearDigit) {
					case 6:
					case 7:
						modifier = +6;
						break;
					case 8:
					case 9:
						modifier = +2;
						break;
				}
				date.setFullYear(year + modifier);
				break;
			}
			case 'home': {
				const firstYearInDecade = year - (year % 10);
				date.setFullYear(firstYearInDecade);
				break;
			}
			case 'end': {
				const firstYearInDecade = year - (year % 10);
				const lastYearInDecade = firstYearInDecade + 9;
				date.setFullYear(lastYearInDecade);
				break;
			}
			case 'pageup':
				date.setFullYear(year - 10);
				break;
			case 'pagedown':
				date.setFullYear(year + 10);
				break;
			default:
				return null;
		}

		return date;
	}
}
